export type ConditionalTabRouteData = {
  featureFlag?: string;
  visibleOnActivatedFeatureFlag?: boolean;
  hideWhenAllFeatureFlagsActive?: string[];
  showWhenAllFeatureFlagsActive?: string[];
};

/**
 * Resolves whether a LaunchDarkly flag value is active.
 * @param flags LaunchDarkly flag map
 * @param key Feature flag key
 * @returns Whether the flag is active
 */
export function isFeatureFlagActive(flags: Record<string, unknown>, key: string): boolean {
  const value = flags[key];
  if (value != null && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'current')) {
    return !!(value as { current: unknown }).current;
  }
  return !!value;
}

/**
 * Returns whether every listed feature flag is active.
 * @param flags LaunchDarkly flag map
 * @param keys Feature flag keys
 * @returns Whether all flags are active
 */
export function areAllFeatureFlagsActive(flags: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => isFeatureFlagActive(flags, key));
}

/**
 * Computes conditional tab visibility from route data and LaunchDarkly flags.
 * @param routeData Route `data` for a conditional tab child
 * @param flags LaunchDarkly flag map
 * @returns Whether the tab should be visible
 */
export function isConditionalTabVisible(
  routeData: ConditionalTabRouteData,
  flags: Record<string, unknown>
): boolean {
  let visible: boolean;
  const featureFlag = routeData.featureFlag;

  if (!featureFlag) {
    visible = true;
  } else {
    const isActivated = isFeatureFlagActive(flags, featureFlag);
    const visibleOnActivated = routeData.visibleOnActivatedFeatureFlag ?? true;
    visible = (isActivated && visibleOnActivated) || (!isActivated && !visibleOnActivated);
  }

  if (
    routeData.hideWhenAllFeatureFlagsActive?.length &&
    areAllFeatureFlagsActive(flags, routeData.hideWhenAllFeatureFlagsActive)
  ) {
    visible = false;
  }

  if (
    routeData.showWhenAllFeatureFlagsActive?.length &&
    areAllFeatureFlagsActive(flags, routeData.showWhenAllFeatureFlagsActive)
  ) {
    visible = true;
  }

  return visible;
}

/**
 * Collects unique feature-flag keys referenced by conditional tab route data.
 * @param routeData Route `data` for a conditional tab child
 * @returns Unique feature flag keys
 */
export function collectConditionalTabFeatureFlags(routeData: ConditionalTabRouteData): string[] {
  return [
    routeData.featureFlag,
    ...(routeData.hideWhenAllFeatureFlagsActive ?? []),
    ...(routeData.showWhenAllFeatureFlagsActive ?? []),
  ].filter((key): key is string => !!key);
}
